import {
  DIFFICULTY_HINTS,
  DIFFICULTY_LABELS,
  NORMALIZED_DIFFICULTIES,
  PLATFORMS,
  PLATFORM_LABELS,
  formatMinutes,
  suggestDifficulty,
  type CreateProblemInput,
  type NormalizedDifficulty,
  type Platform,
  type ProblemView,
  type TopicView,
} from '@algo/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { useMemo, useState } from 'react';
import { Markdown } from '../components/Markdown';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorState,
  Field,
  Input,
  Modal,
  PageHeader,
  Select,
  Spinner,
  Textarea,
} from '../components/ui';
import { INVALIDATE_ON_WRITE, api, qk } from '../lib/api';

const DIFFICULTY_TONE: Record<NormalizedDifficulty, 'success' | 'accent' | 'warning' | 'danger'> = {
  EASY: 'success',
  MEDIUM: 'accent',
  HARD: 'warning',
  EXPERT: 'danger',
};

interface FormState {
  name: string;
  platform: Platform;
  url: string;
  platformDifficulty: string;
  normalizedDifficulty: NormalizedDifficulty;
  topicIds: string[];
  logSolve: boolean;
  minutesSpent: string;
  explanation: string;
  code: string;
  language: string;
  newConcept: string;
  mistakeMade: string;
  optimizationIdea: string;
}

const emptyForm = (): FormState => ({
  name: '',
  platform: 'CODEFORCES',
  url: '',
  platformDifficulty: '',
  normalizedDifficulty: 'MEDIUM',
  topicIds: [],
  logSolve: true,
  minutesSpent: '30',
  explanation: '',
  code: '',
  language: 'cpp',
  newConcept: '',
  mistakeMade: '',
  optimizationIdea: '',
});

function TopicPicker({
  topics,
  selected,
  onToggle,
}: {
  topics: TopicView[];
  selected: string[];
  onToggle: (id: string) => void;
}) {
  return (
    <div className="max-h-40 overflow-y-auto rounded-lg border border-edge bg-raised p-2">
      <div className="flex flex-wrap gap-1.5">
        {topics.map((topic) => {
          const active = selected.includes(topic.id);
          return (
            <button
              key={topic.id}
              type="button"
              onClick={() => onToggle(topic.id)}
              className={clsx(
                'rounded-full border px-2.5 py-1 text-xs transition',
                active
                  ? 'border-accent bg-accent/15 text-accent'
                  : 'border-edge text-muted hover:text-ink',
              )}
            >
              {topic.name}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function ProblemForm({
  topics,
  onClose,
}: {
  topics: TopicView[];
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [form, setForm] = useState<FormState>(emptyForm);

  const suggestion = suggestDifficulty(form.platform, form.platformDifficulty);
  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((current) => ({ ...current, [key]: value }));

  const create = useMutation({
    mutationFn: () => {
      const payload: CreateProblemInput = {
        name: form.name.trim(),
        platform: form.platform,
        url: form.url.trim() ? form.url.trim() : null,
        platformDifficulty: form.platformDifficulty.trim(),
        normalizedDifficulty: form.normalizedDifficulty,
        topicIds: form.topicIds,
        ...(form.logSolve
          ? {
              solution: {
                explanation: form.explanation,
                code: form.code.trim() ? form.code : null,
                language: form.code.trim() ? form.language : null,
                minutesSpent: Number(form.minutesSpent) || 0,
                newConcept: form.newConcept.trim() ? form.newConcept : null,
                mistakeMade: form.mistakeMade.trim() ? form.mistakeMade : null,
                optimizationIdea: form.optimizationIdea.trim() ? form.optimizationIdea : null,
              },
            }
          : {}),
      };
      return api.createProblem(payload);
    },
    onSuccess: async () => {
      await Promise.all(
        INVALIDATE_ON_WRITE.map((key) =>
          queryClient.invalidateQueries({ queryKey: key as readonly unknown[] }),
        ),
      );
      onClose();
    },
  });

  return (
    <Modal open title="Бодлого бүртгэх" onClose={onClose} wide>
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Нэр" className="sm:col-span-2">
          <Input
            value={form.name}
            onChange={(event) => set('name', event.target.value)}
            placeholder="Longest Increasing Subsequence"
          />
        </Field>

        <Field label="Платформ">
          <Select
            value={form.platform}
            onChange={(event) => set('platform', event.target.value as Platform)}
          >
            {PLATFORMS.map((platform) => (
              <option key={platform} value={platform}>
                {PLATFORM_LABELS[platform]}
              </option>
            ))}
          </Select>
        </Field>

        <Field label="Холбоос">
          <Input
            value={form.url}
            onChange={(event) => set('url', event.target.value)}
            placeholder="https://…"
          />
        </Field>

        <Field label="Платформын хүндрэл" hint={DIFFICULTY_HINTS[form.platform]}>
          <Input
            value={form.platformDifficulty}
            onChange={(event) => set('platformDifficulty', event.target.value)}
            placeholder="1400 / Medium / ABC-D"
          />
        </Field>

        <Field
          label="Нэгдсэн хүндрэл"
          hint={
            suggestion && suggestion !== form.normalizedDifficulty ? (
              <button
                type="button"
                className="text-accent underline underline-offset-2"
                onClick={() => set('normalizedDifficulty', suggestion)}
              >
                Санал болгож буй: {DIFFICULTY_LABELS[suggestion]} — хэрэглэх
              </button>
            ) : (
              'Өөрөө шийднэ; Онц хүндийг зөвхөн гараар сонгоно.'
            )
          }
        >
          <Select
            value={form.normalizedDifficulty}
            onChange={(event) =>
              set('normalizedDifficulty', event.target.value as NormalizedDifficulty)
            }
          >
            {NORMALIZED_DIFFICULTIES.map((difficulty) => (
              <option key={difficulty} value={difficulty}>
                {DIFFICULTY_LABELS[difficulty]}
              </option>
            ))}
          </Select>
        </Field>

        <Field label="Сэдвүүд" className="sm:col-span-2">
          <TopicPicker
            topics={topics}
            selected={form.topicIds}
            onToggle={(id) =>
              set(
                'topicIds',
                form.topicIds.includes(id)
                  ? form.topicIds.filter((value) => value !== id)
                  : [...form.topicIds, id],
              )
            }
          />
        </Field>
      </div>

      <label className="mt-5 flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={form.logSolve}
          onChange={(event) => set('logSolve', event.target.checked)}
          className="accent-[rgb(var(--accent))]"
        />
        Бодолтыг одоо бүртгэх
      </label>

      {form.logSolve && (
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <Field label="Зарцуулсан минут">
            <Input
              type="number"
              min={0}
              value={form.minutesSpent}
              onChange={(event) => set('minutesSpent', event.target.value)}
            />
          </Field>
          <Field label="Хэл">
            <Input value={form.language} onChange={(event) => set('language', event.target.value)} />
          </Field>
          <Field label="Тайлбар (markdown)" className="sm:col-span-2">
            <Textarea
              rows={4}
              value={form.explanation}
              onChange={(event) => set('explanation', event.target.value)}
              placeholder="Шийдэл хэрхэн ажилладгийг өөрийн үгээр."
            />
          </Field>
          <Field label="Код" className="sm:col-span-2">
            <Textarea
              rows={6}
              value={form.code}
              onChange={(event) => set('code', event.target.value)}
            />
          </Field>
          <Field label="Шинээр сурсан ойлголт">
            <Input
              value={form.newConcept}
              onChange={(event) => set('newConcept', event.target.value)}
            />
          </Field>
          <Field label="Гаргасан алдаа">
            <Input
              value={form.mistakeMade}
              onChange={(event) => set('mistakeMade', event.target.value)}
            />
          </Field>
          <Field label="Оптимизацийн санаа" className="sm:col-span-2">
            <Input
              value={form.optimizationIdea}
              onChange={(event) => set('optimizationIdea', event.target.value)}
            />
          </Field>
        </div>
      )}

      {create.error && (
        <p className="mt-4 text-sm text-danger">{(create.error as Error).message}</p>
      )}

      <div className="mt-6 flex justify-end gap-2">
        <Button variant="ghost" onClick={onClose}>
          Болих
        </Button>
        <Button
          variant="primary"
          disabled={!form.name.trim() || create.isPending}
          onClick={() => create.mutate()}
        >
          {create.isPending ? 'Хадгалж байна…' : 'Бодлого хадгалах'}
        </Button>
      </div>
    </Modal>
  );
}

function ProblemDetail({ problem, onClose }: { problem: ProblemView; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [minutes, setMinutes] = useState('20');
  const [explanation, setExplanation] = useState('');

  const invalidate = () =>
    Promise.all(
      INVALIDATE_ON_WRITE.map((key) =>
        queryClient.invalidateQueries({ queryKey: key as readonly unknown[] }),
      ),
    );

  const addSolve = useMutation({
    mutationFn: () =>
      api.addSolution(problem.id, {
        explanation,
        minutesSpent: Number(minutes) || 0,
      }),
    onSuccess: async () => {
      setExplanation('');
      await invalidate();
      onClose();
    },
  });

  const remove = useMutation({
    mutationFn: () => api.deleteProblem(problem.id),
    onSuccess: async () => {
      await invalidate();
      onClose();
    },
  });

  return (
    <Modal open title={problem.name} onClose={onClose} wide>
      <div className="flex flex-wrap items-center gap-2">
        <Badge tone={DIFFICULTY_TONE[problem.normalizedDifficulty]}>
          {DIFFICULTY_LABELS[problem.normalizedDifficulty]}
        </Badge>
        <Badge>{PLATFORM_LABELS[problem.platform]}</Badge>
        {problem.platformDifficulty && <Badge>{problem.platformDifficulty}</Badge>}
        {problem.topics.map((topic) => (
          <span key={topic.id} className="chip">
            {topic.name}
          </span>
        ))}
        {problem.url && (
          <a
            href={problem.url}
            target="_blank"
            rel="noreferrer"
            className="ml-auto text-sm text-accent underline underline-offset-2"
          >
            Бодлогыг нээх ↗
          </a>
        )}
      </div>

      <h3 className="mt-6 text-xs font-medium uppercase tracking-wide text-muted">
        Оролдлогууд ({problem.attempts})
      </h3>
      <ol className="mt-2 space-y-3">
        {problem.solutions.map((solution, index) => (
          <li key={solution.id} className="rounded-lg border border-edge bg-raised p-4">
            <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-muted">
              <span>
                {index === 0 ? 'Анхны бодолт' : `Дахин бодолт #${index}`} ·{' '}
                {solution.solvedAt.slice(0, 10)} · {formatMinutes(solution.minutesSpent)}
              </span>
              {solution.language && <span className="font-mono">{solution.language}</span>}
            </div>

            {solution.explanation && (
              <div className="mt-3">
                <Markdown>{solution.explanation}</Markdown>
              </div>
            )}

            {solution.code && (
              <pre className="mt-3 overflow-x-auto rounded-lg border border-edge bg-panel p-3 font-mono text-xs">
                <code>{solution.code}</code>
              </pre>
            )}

            <dl className="mt-3 grid gap-2 text-xs sm:grid-cols-3">
              {[
                ['Сурсан зүйл', solution.newConcept],
                ['Алдаа', solution.mistakeMade],
                ['Оптимизаци', solution.optimizationIdea],
              ]
                .filter(([, value]) => Boolean(value))
                .map(([label, value]) => (
                  <div key={label}>
                    <dt className="text-muted">{label}</dt>
                    <dd className="mt-0.5">{value}</dd>
                  </div>
                ))}
            </dl>
          </li>
        ))}
        {problem.solutions.length === 0 && (
          <li className="text-sm text-muted">Хараахан бодоогүй.</li>
        )}
      </ol>

      <div className="mt-6 rounded-lg border border-edge p-4">
        <p className="text-xs font-medium uppercase tracking-wide text-muted">
          Дахин оролдлого бүртгэх
        </p>
        <p className="mt-1 text-xs text-muted">Дахин бодолт нь бодлогын онооны 25%-ийг өгнө.</p>
        <div className="mt-3 flex flex-wrap gap-2">
          <Input
            type="number"
            min={0}
            className="w-28"
            value={minutes}
            onChange={(event) => setMinutes(event.target.value)}
            aria-label="Минут"
          />
          <Input
            className="min-w-[12rem] flex-1"
            placeholder="Энэ удаа юу анзаарав?"
            value={explanation}
            onChange={(event) => setExplanation(event.target.value)}
          />
          <Button disabled={addSolve.isPending} onClick={() => addSolve.mutate()}>
            Оролдлого нэмэх
          </Button>
        </div>
      </div>

      <div className="mt-6 flex justify-between">
        <Button
          variant="danger"
          disabled={remove.isPending}
          onClick={() => remove.mutate()}
        >
          Бодлого устгах
        </Button>
        <Button variant="ghost" onClick={onClose}>
          Хаах
        </Button>
      </div>
    </Modal>
  );
}

export function ProblemsPage() {
  const [search, setSearch] = useState('');
  const [platform, setPlatform] = useState('');
  const [difficulty, setDifficulty] = useState('');
  const [topicId, setTopicId] = useState('');
  const [creating, setCreating] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);

  const filters = useMemo(
    () => ({
      q: search || undefined,
      platform: platform || undefined,
      difficulty: difficulty || undefined,
      topicId: topicId || undefined,
      take: 200,
    }),
    [search, platform, difficulty, topicId],
  );

  const topicsQuery = useQuery({ queryKey: qk.topics, queryFn: () => api.listTopics() });
  const problemsQuery = useQuery({
    queryKey: qk.problems(filters),
    queryFn: () => api.listProblems(filters),
  });

  if (problemsQuery.error) {
    return <ErrorState error={problemsQuery.error} onRetry={() => void problemsQuery.refetch()} />;
  }

  const problems = problemsQuery.data?.items ?? [];
  const topics = topicsQuery.data ?? [];
  const openProblem = problems.find((problem) => problem.id === openId) ?? null;

  return (
    <>
      <PageHeader
        title="Бодлогууд"
        description={
          problemsQuery.data
            ? `${problemsQuery.data.total} бодлого бүртгэлтэй`
            : 'Таны бодсон бүх бодлого, бүх оролдлогын хамт'
        }
        action={
          <Button variant="primary" onClick={() => setCreating(true)}>
            + Бодлого бүртгэх
          </Button>
        }
      />

      <Card bodyClassName="p-3">
        <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-4">
          <Input
            placeholder="Нэрээр хайх…"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
          <Select value={platform} onChange={(event) => setPlatform(event.target.value)}>
            <option value="">Бүх платформ</option>
            {PLATFORMS.map((option) => (
              <option key={option} value={option}>
                {PLATFORM_LABELS[option]}
              </option>
            ))}
          </Select>
          <Select value={difficulty} onChange={(event) => setDifficulty(event.target.value)}>
            <option value="">Бүх хүндрэл</option>
            {NORMALIZED_DIFFICULTIES.map((option) => (
              <option key={option} value={option}>
                {DIFFICULTY_LABELS[option]}
              </option>
            ))}
          </Select>
          <Select value={topicId} onChange={(event) => setTopicId(event.target.value)}>
            <option value="">Бүх сэдэв</option>
            {topics.map((topic) => (
              <option key={topic.id} value={topic.id}>
                {topic.name}
              </option>
            ))}
          </Select>
        </div>
      </Card>

      <div className="mt-4">
        {problemsQuery.isLoading ? (
          <Spinner label="Бодлогуудыг ачаалж байна…" />
        ) : problems.length === 0 ? (
          <Card>
            <EmptyState
              title="Тохирох бодлого олдсонгүй"
              description="Шүүлтүүрээ өөрчлөх эсвэл дөнгөж бодсон бодлогоо бүртгэ."
              action={
                <Button variant="primary" onClick={() => setCreating(true)}>
                  + Бодлого бүртгэх
                </Button>
              }
            />
          </Card>
        ) : (
          <Card bodyClassName="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="border-b border-edge text-xs uppercase tracking-wide text-muted">
                  <tr>
                    <th className="px-5 py-2.5 font-medium">Бодлого</th>
                    <th className="px-3 py-2.5 font-medium">Хүндрэл</th>
                    <th className="px-3 py-2.5 font-medium">Сэдвүүд</th>
                    <th className="px-3 py-2.5 font-medium">Оролдлого</th>
                    <th className="px-5 py-2.5 font-medium">Анх бодсон</th>
                  </tr>
                </thead>
                <tbody>
                  {problems.map((problem) => (
                    <tr
                      key={problem.id}
                      onClick={() => setOpenId(problem.id)}
                      className="cursor-pointer border-b border-edge/60 transition last:border-0 hover:bg-raised"
                    >
                      <td className="px-5 py-2.5">
                        <span className="font-medium">{problem.name}</span>
                        <span className="ml-2 text-xs text-muted">
                          {PLATFORM_LABELS[problem.platform]}
                        </span>
                      </td>
                      <td className="px-3 py-2.5">
                        <Badge tone={DIFFICULTY_TONE[problem.normalizedDifficulty]}>
                          {DIFFICULTY_LABELS[problem.normalizedDifficulty]}
                        </Badge>
                        {problem.platformDifficulty && (
                          <span className="ml-2 font-mono text-xs text-muted">
                            {problem.platformDifficulty}
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2.5 text-xs text-muted">
                        {problem.topics.map((topic) => topic.name).join(', ') || '—'}
                      </td>
                      <td className="px-3 py-2.5 font-mono text-xs">{problem.attempts}</td>
                      <td className="px-5 py-2.5 text-xs text-muted">
                        {problem.firstSolvedAt?.slice(0, 10) ?? 'Бодоогүй'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}
      </div>

      {creating && <ProblemForm topics={topics} onClose={() => setCreating(false)} />}
      {openProblem && <ProblemDetail problem={openProblem} onClose={() => setOpenId(null)} />}
    </>
  );
}
